import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import UserInfo from '../../../../interfaces/UserInfo';

const MentionList = forwardRef((props: any, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => setSelectedIndex(0), [props.items]);

    const selectItem = (index: number) => {
        const item = props.items[index];
        if (item) {
            props.command({ id: item.id, label: item.displayName });
        }
    };

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            if (event.key === 'ArrowUp') {
                setSelectedIndex((prev) => (prev + props.items.length - 1) % props.items.length);
                return true;
            }
            if (event.key === 'ArrowDown') {
                setSelectedIndex((prev) => (prev + 1) % props.items.length);
                return true;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                selectItem(selectedIndex);
                return true;
            }
            return false;
        },
    }));

    return (
        <div className="chat-mention-suggestion bg-white rounded-xl shadow-xl overflow-hidden flex flex-col w-64 p-1 animate-in fade-in zoom-in-95 duration-100">
            {props.items.length > 0 ? (
                props.items.map((item: UserInfo, index: number) => (
                    <button
                        key={item.id}
                        onClick={() => selectItem(index)}
                        className={`chat-mention-suggestion-option flex items-center gap-3 w-full text-left px-3 py-2 text-[13px] rounded-lg transition-colors ${
                            index === selectedIndex
                            ? 'chat-mention-suggestion-option-active bg-purple-50 text-[#6847A3]'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                    >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                            index === selectedIndex ? 'bg-white border-indigo-200' : 'bg-slate-100 border-slate-200'
                        }`}>
                            {item.firstName?.[0]}{item.lastName?.[0]}
                        </div>
                        <span className="truncate font-medium">{item.displayName}</span>
                    </button>
                ))
            ) : (
                <div className="px-3 py-3 text-sm text-slate-400 italic text-center">No users found</div>
            )}
        </div>
    );
});

export default function getMentionSuggestions(getUsers: () => UserInfo[]) {
    return {
        char: '@',
        items: ({ query }: { query: string }) => {
            const currentUsers = getUsers() || [];
            return currentUsers
                .filter((user) => user.displayName.toLowerCase().includes(query.toLowerCase()))
                .slice(0, 5);
        },
        render: () => {
            let component: ReactRenderer<any>;
            let popup: any[];

            return {
                onStart: (props: any) => {
                    component = new ReactRenderer(MentionList, {
                        props,
                        editor: props.editor,
                    });

                    if (!props.clientRect) return;

                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'top-start',
                    });
                },
                onUpdate(props: any) {
                    component.updateProps(props);
                    if (!props.clientRect) return;
                    popup[0].setProps({
                        getReferenceClientRect: props.clientRect,
                    });
                },
                onKeyDown(props: any) {
                    if (props.event.key === 'Escape') {
                        popup[0].hide();
                        return true;
                    }
                    return (component.ref as any)?.onKeyDown(props);
                },
                onExit() {
                    if (popup && popup[0]) popup[0].destroy();
                    if (component) component.destroy();
                },
            };
        },
    };
}
